import { useParams } from "react-router";
import { useState, useMemo } from "react";
import { MODELS, useMultiModel } from "../../../api";
import type { SparkModule } from "../../../api/spark";
import NotFoundPage from "../../not-found/NotFoundPage.jsx";
import { injectGlossaryMarks } from "../../../design-system/components/BlockContent/glossary";
import { GlossaryDescriptionsCtx } from "../../../design-system/components/BlockContent/components/GlossaryEntry/GlossaryDescriptionsCtx";
import type { AnySparkBag } from "../sparkBag";
import { SparkStepOverviewWithData } from "./SparkStepOverviewWithData";
import { hasRole, Roles } from "../../../lib/Roles";
import Overlay from "./components/Overlay/Overlay";
import { useLernreiseCompletionCheck } from "./hooks/useLernreiseCompletionCheck";
import Toast from "../../../design-system/primitives/components/Toast/Toast";

interface SparkStepOverviewProps {
  /** The spark data, handed down by the shell rather than read off a context. */
  bag: AnySparkBag;
}

/**
 * A step of a spark: fetches the step named in the url, its content and its
 * neighbours in the stage, and renders them.
 *
 * Serves a Lernreise and the demo alike. The demo opens a spark without a
 * workshop, so the parts of it marked as needing one are off limits: the
 * overview greys them out (`disableSome`), and here they drop out of the
 * prev/next sequence and 404 when opened directly.
 */
const SparkStepOverview = ({
  bag: { workshop, spark, structure },
}: SparkStepOverviewProps) => {
  const { stepSlug } = useParams<{ stepSlug: SparkModule["slug"] }>();
  const [me, currStep] = useMultiModel([
    MODELS.ME,
    // Always present: this only mounts on the `step/:stepSlug` route.
    MODELS.SPARK_SCHEDULE_STEP(spark.slug, stepSlug ?? ""),
  ]);

  const stageSlug = currStep.stageSlug;

  const [stageSteps, content, glossaryTerms] = useMultiModel([
    MODELS.SPARK_SCHEDULE_STEPS(spark.slug, stageSlug),
    currStep.content?.contentKey
      ? MODELS.SPARK_MODULE_CONTENT(currStep.content.contentKey)
      : null,
    spark.showGlossary ? MODELS.SPARK_GLOSSARY_TERMS(spark.slug) : null,
  ]);

  const hideDisabled = workshop === null;
  const allCurrentStageSteps = useMemo(
    () =>
      hideDisabled
        ? stageSteps.filter(
            (step) =>
              !step.disabled && step.content?.station?.disabled !== true,
          )
        : stageSteps,
    [hideDisabled, stageSteps],
  );

  // Highlight glossary terms in the content up front; `foundTerms` are the
  // canonical terms actually marked, which we then load descriptions for.
  const { content: processedContent, terms: foundTerms } = useMemo(
    () =>
      glossaryTerms && Object.keys(glossaryTerms).length && content
        ? injectGlossaryMarks(content, glossaryTerms)
        : { content, terms: [] },
    [content, glossaryTerms],
  );

  // Non-suspending: the step renders immediately (highlights included) and the
  // tooltip descriptions pop in once this lands. Skipped when nothing matched.
  const [glossaryDescriptions] = useMultiModel(
    [
      foundTerms.length
        ? MODELS.SPARK_GLOSSARY_DESCRIPTIONS(spark.slug, foundTerms)
        : null,
    ],
    { lazy: true },
  );

  const glossaryDescriptionMap = useMemo(
    () =>
      glossaryDescriptions
        ? new Map(glossaryDescriptions.map((d) => [d.term, d]))
        : null,
    [glossaryDescriptions],
  );

  // The step's place in the spark, outermost first: phase, then stage.
  const { phase, stage, breadcrumb } = useMemo(() => {
    const foundPhase =
      stageSlug && structure
        ? structure.find((p) => p.stages?.some((s) => s.slug === stageSlug))
        : undefined;
    const foundStage = foundPhase?.stages.find((s) => s.slug === stageSlug);
    return {
      phase: foundPhase,
      stage: foundStage,
      breadcrumb: [foundPhase?.name, foundStage?.name].filter(
        (name) => name !== undefined,
      ),
    };
  }, [stageSlug, structure]);

  const stationSteps = useMemo(() => {
    if (!currStep.content?.station?._key || !(allCurrentStageSteps.length > 0))
      return null;
    return allCurrentStageSteps.filter(
      (step) => step.content?.station?._key === currStep.content?.station?._key,
    );
  }, [currStep, allCurrentStageSteps]);

  const { nextStep, prevStep } = useMemo(() => {
    if (!stepSlug || !(allCurrentStageSteps.length > 0))
      return { nextStep: null, prevStep: null };
    const currStepIdx = allCurrentStageSteps.findIndex(
      (step) => step.slug === stepSlug,
    );
    return {
      nextStep: allCurrentStageSteps[currStepIdx + 1] ?? null,
      prevStep: currStepIdx > 0 ? allCurrentStageSteps[currStepIdx - 1] : null,
    };
  }, [stepSlug, allCurrentStageSteps]);

  const workshopId = workshop?.id;
  const preventOverlay = Boolean(
    !workshopId ||
    !currStep.sparkStateQuery ||
    workshop.state === "completed" ||
    !hasRole(workshop.roles, Roles.TEACHER) ||
    !me ||
    me.isAnon ||
    !me.email ||
    me.isAdmin,
  );

  const { isDueNow } = useLernreiseCompletionCheck(workshopId);
  const [checkDue] = useState(() => Boolean(workshopId) && isDueNow());
  const [dismissed, setDismissed] = useState(false);
  const [saveErrorOpen, setSaveErrorOpen] = useState(false);
  const openOverlay = checkDue && !dismissed && !preventOverlay;

  if (
    hideDisabled &&
    (currStep.disabled ||
      currStep.content?.station?.disabled ||
      phase?.disabled ||
      stage?.disabled)
  ) {
    return <NotFoundPage />;
  }

  return (
    <GlossaryDescriptionsCtx.Provider value={glossaryDescriptionMap}>
      {openOverlay && workshop && (
        <Overlay
          workshopId={workshop.id}
          workshopSlug={workshop.slug}
          onClose={() => setDismissed(true)}
          onSaveError={() => setSaveErrorOpen(true)}
        />
      )}
      <Toast
        tone="error"
        open={saveErrorOpen}
        onOpenChange={setSaveErrorOpen}
        icon="circle-alert"
        title="Deine Antwort konnte nicht gespeichert werden."
        description="Du kannst trotzdem weitermachen."
        closeLabel="Schließen"
        duration={Infinity}
      />
      <SparkStepOverviewWithData
        step={currStep}
        content={processedContent}
        stationSteps={stationSteps}
        nextStep={nextStep}
        prevStep={prevStep}
        breadcrumb={breadcrumb}
      />
    </GlossaryDescriptionsCtx.Provider>
  );
};

export default SparkStepOverview;
